import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { getScoreColor, calculateGaugeProperties } from './driver-score-utils';

/**
 * Props for the DriverScoreGauge component.
 */
interface DriverScoreGaugeProps {
	/** Score value to display (0-100). Values outside this range are clamped. */
	score: number;
	/** Size of the gauge in pixels (width and height). Default: 120 */
	size?: number;
}

/**
 * Circular SVG gauge for visualizing driver scores.
 *
 * Renders an animated circular progress indicator with the score value
 * displayed in the center. The progress arc color changes based on the
 * score threshold (green >= 70, amber >= 50, red < 50).
 *
 * @component
 * @example
 * ```tsx
 * // Default size
 * <DriverScoreGauge score={75} />
 *
 * // Custom size
 * <DriverScoreGauge score={85} size={150} />
 * ```
 */
const DriverScoreGauge: React.FC<DriverScoreGaugeProps> = ({ score, size = 120 }) => {
	const theme = useTheme();
	const strokeWidth = 8;

	const { radius, circumference, strokeDashoffset } = calculateGaugeProperties(score, size, strokeWidth);
	const color = getScoreColor(score, theme);

	return (
		<Box sx={{ position: 'relative', width: size, height: size }}>
			<svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
				{/* Background circle */}
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill='none'
					stroke={theme.palette.grey[300]}
					strokeWidth={strokeWidth}
				/>
				{/* Progress circle */}
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill='none'
					stroke={color}
					strokeWidth={strokeWidth}
					strokeDasharray={circumference}
					strokeDashoffset={strokeDashoffset}
					strokeLinecap='round'
					style={{ transition: 'stroke-dashoffset 0.5s ease' }}
				/>
			</svg>
			{/* Score text in center */}
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography
					sx={{
						fontSize: '2rem',
						fontWeight: 'bold',
						color: theme.palette.text.primary,
						lineHeight: 1,
					}}
				>
					{Math.round(score)}
				</Typography>
				<Typography
					sx={{
						fontSize: '0.75rem',
						color: theme.palette.text.secondary,
					}}
				>
					out of 100
				</Typography>
			</Box>
		</Box>
	);
};

export default DriverScoreGauge;
